import { forwardRef, useEffect, useState } from 'react';

import { fromStringDateToProcurosTimestamp } from 'support/helpers/dateTime/dateTime';
import { classNames } from 'support/helpers/generic/generic';
import { useDataTableContext } from 'components/DataTable/contexts/DataTableContext';

import { Input } from '../Input';

type TimestampDateInputProps = {
  name: string;
  inputWrapperClassName?: string;
};

const useLocalValue = (externalValue: HTMLInputElement['value'] | Parameters<typeof Input>[0]['value']) => {
  const [localValue, setLocalValue] = useState(externalValue ?? '');

  useEffect(() => {
    setLocalValue(externalValue ?? '');
  }, [externalValue]);

  return [localValue, setLocalValue] as const;
};

export const TimestampDateInput = forwardRef<HTMLInputElement, Parameters<typeof Input>[0] & TimestampDateInputProps>(
  (props, ref) => {
    const { name, value, className, wrapperClassName, onBlur, inputWrapperClassName } = props;
    const { register, setValue } = useDataTableContext();
    const registerReturn = register(name, { valueAsNumber: true });
    const [localValue, setLocalValue] = useLocalValue(value ?? '');

    return (
      <div className={classNames('flex size-full', wrapperClassName)}>
        <input type="number" className="hidden" {...registerReturn} ref={ref}></input>
        <Input
          {...props}
          wrapperClassName={classNames('flex-1', wrapperClassName)}
          inputWrapperClassName={inputWrapperClassName}
          id={undefined}
          name={undefined}
          type="date"
          value={localValue}
          className={classNames(className)}
          onChange={(e) => {
            setLocalValue(e.target.value);
          }}
          onBlur={(e) => {
            const input = e.target as HTMLInputElement;
            if (input.value) {
              setValue(name, fromStringDateToProcurosTimestamp(input.value), {
                shouldDirty: true,
              });
            }
            onBlur?.(e);
          }}
        />
      </div>
    );
  },
);

TimestampDateInput.displayName = 'TimestampDateInput';
